/**
 * wrapt-cwv.js — Core Web Vitals from real visits, with what caused them
 * (CWV handoff, 9 Oct 2026, Task 4; D128).
 *
 * Search Console only says that a group of pages is slow or jumpy, never
 * which element. This reports each visit's LCP, CLS and INP with the element
 * responsible, so the culprit can be named from field data.
 *
 * web-vitals 6.2.3, the attribution build, is vendored beside this file as
 * wrapt-web-vitals-6.2.3.js (Apache-2.0); no CDN at runtime. Each report is
 * published to Shopify's customer-events bus as `web_vitals`. Nothing leaves
 * the site until a custom pixel (Settings › Customer events) subscribes to
 * that event and forwards it, to GA4, Klaviyo or wherever Alan chooses. The
 * pixel's code is in docs/cwv/CUSTOM-PIXEL.md.
 *
 * The payload, per metric:
 *   name, value, rating ('good' | 'needs-improvement' | 'poor'),
 *   target (a CSS selector for the element), url (the path), id, nav
 *   LCP adds ttfb, loadDelay, loadDuration, renderDelay (ms) and resource
 *   CLS adds shiftTime, shiftValue and loadState
 *   INP adds type, inputDelay, processing, presentation (ms) and loadState
 */

import { onCLS, onINP, onLCP } from './wrapt-web-vitals-6.2.3.js';

/* Share of visits that report: 1 is every visit. Lower it if the volume is
   ever a cost where the pixel sends it. */
const SAMPLE = 1;

const ms = (n) => (typeof n === 'number' && isFinite(n) ? Math.round(n) : null);

/* Shopify's web pixels manager defines `analytics.publish` from the page's
   content_for_header. A report that arrives first waits for it, briefly. */
const queue = [];
let waiting = false;
function flush() {
  const api = window.Shopify && window.Shopify.analytics;
  if (!api || typeof api.publish !== 'function') return false;
  while (queue.length) api.publish('web_vitals', queue.shift());
  return true;
}
function publish(payload) {
  queue.push(payload);
  if (flush() || waiting) return;
  waiting = true;
  let tries = 0;
  const timer = setInterval(() => {
    if (flush() || ++tries >= 20) {
      clearInterval(timer);
      waiting = false;
    }
  }, 500);
}

function report(metric) {
  const a = metric.attribution || {};
  const data = {
    name: metric.name,
    value: metric.name === 'CLS' ? Math.round(metric.value * 10000) / 10000 : ms(metric.value),
    rating: metric.rating,
    target: (metric.name === 'CLS' ? a.largestShiftTarget : metric.name === 'INP' ? a.interactionTarget : a.target) || '',
    url: location.pathname,
    id: metric.id,
    nav: metric.navigationType,
  };
  if (metric.name === 'LCP') {
    Object.assign(data, {
      ttfb: ms(a.timeToFirstByte),
      loadDelay: ms(a.resourceLoadDelay),
      loadDuration: ms(a.resourceLoadDuration),
      renderDelay: ms(a.elementRenderDelay),
      resource: a.lcpResourceEntry ? String(a.lcpResourceEntry.name).split('?')[0].slice(-100) : '',
    });
  } else if (metric.name === 'CLS') {
    Object.assign(data, { shiftTime: ms(a.largestShiftTime), shiftValue: a.largestShiftValue ?? null, loadState: a.loadState || '' });
  } else if (metric.name === 'INP') {
    Object.assign(data, {
      type: a.interactionType || '',
      inputDelay: ms(a.inputDelay),
      processing: ms(a.processingDuration),
      presentation: ms(a.presentationDelay),
      loadState: a.loadState || '',
    });
  }
  publish(data);
}

if (Math.random() < SAMPLE) {
  onCLS(report);
  onLCP(report);
  onINP(report);
}
